import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { SearchClient } from 'algoliasearch/lite';
import isEqual from 'lodash/isEqual';
import type { ReactElement } from 'react';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  InstantSearchProps,
  InstantSearchServerState,
} from 'react-instantsearch-core';
import {
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-core';

import { PlaceholderProductListing } from '@/components/Cms/PlaceholderProductListing';
import type {
  CombinedCollectionGroupItem,
  ComponentChunk,
  EditorialSectionOpts,
} from '@/components/CollectionGroup/types';
import { HeroProductListing } from '@/components/HeroProductListing';
import type { ContentfulSelectorModalByMarketingPartnerQuery } from '@/graphql/contentful/api';
import { ListingPageDocument } from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import {
  buildQueryParamsFromSearchUiState,
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import { PAGE_CONTEXT, type SearchUiState } from '@/integrations/algolia/types';
import type { ContentfulData } from '@/integrations/contentful';
// Be sure to import this:
import {
  getHeroProductListingType,
  renderContentfulSection,
} from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { getRegionFromLocale } from '@/lib/regions';
import { listingActions } from '@/states/listingState';
import { buildEditorialComponentChunks } from '@/views/EditorialView/data-helpers';

import CollectionGroup from '../../components/CollectionGroup/CollectionGroup';
import MarketingSelectorModal from '../WatchView/components/MarketingSelectorModal/MarketingSelectorModal';
import SearchConfiguration from './SearchConfiguration';

export interface EditorialViewProps {
  algoliaCategoryId: string;
  algoliaFilters: string;
  algoliaRuleName: string;
  /** SSR-generated Algolia user token for consistent rendering (BEC-20960) */
  algoliaUserToken?: string;
  cmsDefaultLayoutData?: ContentfulData[];
  draftMode?: boolean;
  editorialCmsData: ContentfulData[];
  instantSearchProps?: Partial<InstantSearchProps>;
  marketingSelectorModalResult:
    | NonNullable<
        ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
      >['items'][0]
    | null;
  pageName: string;
  resolvedCollectionGroups?: CombinedCollectionGroupItem[] | null;
  serverState?: InstantSearchServerState;
}

const EditorialView = ({
  algoliaCategoryId,
  algoliaFilters,
  algoliaRuleName,
  algoliaUserToken,
  draftMode,
  editorialCmsData,
  instantSearchProps,
  marketingSelectorModalResult,
  pageName,
  resolvedCollectionGroups,
  serverState,
}: EditorialViewProps) => {
  const liveEditorialCmsData = useContentfulLiveUpdates(editorialCmsData, {
    query: ListingPageDocument,
  });
  const { asPath, locale, push, query } = useRouter();
  const pageIndex = query?.page && parseInt(query.page as string);
  const indexName = getIndexName('PRODUCT', locale);
  const lastUiState = useRef<SearchUiState | null>(null);
  const [isMarketingSelectorModalOpen, setIsMarketingSelectorModalOpen] =
    useState(false);

  useEffect(() => {
    const handleOpenSelectorModal = () => {
      setIsMarketingSelectorModalOpen(true);
    };

    document.body.addEventListener(
      'openSelectorModal',
      handleOpenSelectorModal,
    );

    return () => {
      document.body.removeEventListener(
        'openSelectorModal',
        handleOpenSelectorModal,
      );
    };
  }, []);

  const uiStateFromURL = useMemo(() => {
    const region = getRegionFromLocale(locale);
    return buildUiStateFromURL(
      asPath,
      indexName,
      undefined,
      undefined,
      region.locale,
      serverState,
      'EN_US',
    );
  }, [asPath, indexName, locale, serverState]);

  const syncStateWithUrl = useCallback(
    (uiState: SearchUiState) => {
      const queryParams = buildQueryParamsFromSearchUiState(
        uiState,
        indexName,
        query,
        serverState,
        'EN_US',
      );

      const cleanPathname = asPath.split('?')[0];
      push({ pathname: cleanPathname, query: queryParams }, undefined, {
        shallow: true,
      });
    },
    [indexName, push, query, serverState, asPath],
  );

  useEffect(() => {
    listingActions.updateListingType('WATCH');
  }, []);

  const sectionOpts: EditorialSectionOpts = [
    {
      type: 'Placeholder',
      opts: {
        type: 'product listing',
      },
    },
    {
      type: 'HeroProductListing',
      opts: {
        type: 'watch editorial page',
        pageName: pageName,
      },
    },
  ];

  const componentChunks = buildEditorialComponentChunks(
    liveEditorialCmsData,
    sectionOpts,
  );

  const hasCollectionGroups = liveEditorialCmsData.some(
    (item) => item.__typename === 'CollectionGroup',
  );

  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_WATCHES,
  });

  // Destructure to remove initialUiState and serverState from instantSearchProps
  // to prevent them from overriding our translated uiStateFromURL and serverState prop
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initialUiState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    serverState: _serverStateFromProps,
    ...restInstantSearchProps
  } = (instantSearchProps as any) || {};

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        indexName={indexName}
        initialUiState={uiStateFromURL}
        insights={false}
        onStateChange={({ setUiState, uiState }) => {
          if (!isEqual(uiState, lastUiState.current)) {
            lastUiState.current = uiState;
            setUiState(uiState);
            syncStateWithUrl(uiState);
          }
        }}
        searchClient={
          hasCollectionGroups
            ? ({
                search: (requests: any) =>
                  Promise.resolve({
                    results: requests.map(() => ({
                      hits: [],
                      nbHits: 0,
                      nbPages: 0,
                      page: 0,
                      facets: [],
                      processingTimeMS: 0,
                      hitsPerPage: 0,
                      exhaustiveNbHits: false,
                      query: '',
                      params: '',
                    })),
                  }),
              } as unknown as SearchClient)
            : algoliaSearchClient
        }
        {...restInstantSearchProps}
      >
        {componentChunks.map((chunk: ComponentChunk, chunkIndex) => {
          switch (chunk.type) {
            case 'collection-group': {
              const matchedResolvedGroups = (
                resolvedCollectionGroups ?? []
              ).filter((resolvedGroup) =>
                chunk.items.find(
                  (originalGroup) =>
                    originalGroup.sys.id === resolvedGroup.sys.id,
                ),
              );

              return (
                <CollectionGroup
                  key={`collection-group-${chunkIndex}`}
                  collectionGroupItems={matchedResolvedGroups}
                />
              );
            }

            case 'contentful-section':
              return (
                <Fragment key={`section-${chunkIndex}`}>
                  {renderContentfulSection(
                    chunk.item,
                    sectionOpts,
                    undefined,
                    draftMode,
                  )}
                </Fragment>
              );

            case 'hero-product-listing':
              return (
                <HeroProductListing
                  key={chunk.item.sys.id}
                  data-test-id='EditorialViewHeroProductListing'
                  {...chunk.item}
                  {...chunk.opts}
                  pageName={chunk.opts?.pageName ?? ''}
                  type={getHeroProductListingType(
                    chunk.item,
                    chunk.opts,
                    chunk.opts?.pageName,
                  )}
                />
              );

            case 'placeholder':
              return chunk.opts ? (
                <PlaceholderProductListing
                  key={chunk.item.sys.id}
                  data-test-id='PlaceholderProductListing'
                  {...chunk.item}
                  {...chunk.opts}
                  productType='watch'
                />
              ) : null;

            default:
              return null;
          }
        })}

        <MarketingSelectorModal
          close={() => setIsMarketingSelectorModalOpen(false)}
          data-test-id='MarketingSelectorModal'
          isOpen={isMarketingSelectorModalOpen}
          marketingSelectorModalResult={marketingSelectorModalResult}
          onClose={() => setIsMarketingSelectorModalOpen(false)}
        />

        {!hasCollectionGroups && (
          <SearchConfiguration
            algoliaCategoryId={algoliaCategoryId}
            algoliaFilters={algoliaFilters}
            algoliaRuleName={algoliaRuleName}
            asPath={asPath}
            pageIndex={pageIndex}
            uiStateFromURL={uiStateFromURL}
            userToken={algoliaUserToken}
          />
        )}
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

EditorialView.getLayout = (page: ReactElement) => (
  <Default
    cmsData={page.props.cmsDefaultLayoutData}
    draftMode={page.props.draftMode}
  >
    {page}
  </Default>
);

export default EditorialView;
